import React, { useCallback } from 'react';
import Card from '@mui/material/Card';
import Box from '@mui/material/Box';
import Alert from '@mui/material/Alert';
import InventoryIcon from '@mui/icons-material/Inventory';
import TrackChangesIcon from '@mui/icons-material/TrackChanges';

import { SURFACE_BG } from '@/constants/colors';
import { SectionHeading } from '@/components';
import { useBushingStore } from '@/stores/useBushingStore';
import { useInputStore } from '@/stores/useInputStore';
import { useConfigStore } from '@/stores/useConfigStore';
import { useUIStore } from '@/stores/useUIStore';
import { isByd } from '@/constants/bushingTypes';
import { useSORUpload } from '@/hooks/useSORUpload';
import { useInputValidation } from '@/hooks/useInputValidation';
import { useComputeValidation } from '@/hooks/useComputeValidation';
import { useCoreOdMin } from '@/hooks/useCoreOdMin';
import { useCoreOdMax } from '@/hooks/useCoreOdMax';
import { useC1HDefault } from '@/hooks/useH';
import { useParameterRanges } from '@/hooks/useParameterRanges';
import { useComputeExecution } from '@/hooks/useComputeExecution';
import { useAutoClamp } from '@/hooks/useAutoClamp';

import SORUpload from './SORUpload';
import SORComboBox from './SORComboBox';
import MissingSORDialog from './MissingSORDialog';
import PackagingInputs from './PackagingInputs';
import CoreODGroup from './CoreODGroup';
import CollarODRow from './CollarODRow';
import CompoundFamilyComboBox from './CompoundFamilyComboBox';
import TargetGrid from './TargetGrid';
import ShoreSlider from './ShoreSlider';
import ComputeButton from './ComputeButton';
import ConfigDialog from './ConfigDialog';

import {
  ComputeConfirmDialog,
  ComputeGuardDialog,
  BushingTypeChangeDialog,
} from '@/features/dialogs';

/**
 * InputPanel
 *
 * Left column of the OptiBush UI containing:
 *  - SOR upload
 *  - SOR number and bushing type selection
 *  - Packaging inputs
 *  - Target inputs
 *  - Configuration
 *  - Compute action
 */
const InputPanel: React.FC = () => {
  /* ── Store selectors ──────────────────────────────────── */

  const bushingType = useBushingStore((state) => state.bushingType);
  const config = useBushingStore((state) => state.config);

  const packaging = useInputStore((state) => state.packaging);
  const targets = useInputStore((state) => state.target.targets);
  const amplitudes = useInputStore((state) => state.target.amplitudes);
  const linearTravel = useInputStore(
    (state) => state.target.linearTravel,
  );
  const shoreA = useInputStore((state) => state.shoreA);
  const compoundFamily = useInputStore(
    (state) => state.compoundFamily,
  );
  const sleeveThickness = useInputStore(
    (state) => state.sleeveThickness,
  );
  const computeDirty = useInputStore(
    (state) => state.computeDirty,
  );
  const sorString = useInputStore((state) => state.sorString);

  /*
   * Configuration changes also require a new compute.
   *
   * A dirty configuration must enable Compute rather than disable it.
   */
  const configDirty = useConfigStore((state) => state.dirty);

  const setPackagingField = useInputStore(
    (state) => state.setPackagingField,
  );
  const setTarget = useInputStore((state) => state.setTarget);
  const setAmplitude = useInputStore((state) => state.setAmplitude);
  const setLinearTravel = useInputStore(
    (state) => state.setLinearTravel,
  );
  const setShoreA = useInputStore((state) => state.setShoreA);
  const setCompoundFamily = useInputStore(
    (state) => state.setCompoundFamily,
  );
  const setSleeveThickness = useInputStore(
    (state) => state.setSleeveThickness,
  );

  /* ── Hooks ────────────────────────────────────────────── */

  const {
    fileName,
    loading: sorLoading,
    error: sorError,
    sorItems,
    handleFile,
    handleSorSelect,
    handleBushingTypeChange,
    clearFile,
    missingData,
    confirmBushingTypeChange,
  } = useSORUpload();

  const dynamicRanges = useParameterRanges();

  const validation = useInputValidation(
    packaging,
    targets,
    linearTravel,
    sleeveThickness,
    bushingType,
    dynamicRanges,
  );

  const { validate } = useComputeValidation();
  const { computing, doCompute } = useComputeExecution();

  /* ── Side-effect hooks ────────────────────────────────── */

  useCoreOdMin();
  useCoreOdMax(dynamicRanges.packaging.coreOdMax);
  useC1HDefault();
  useAutoClamp(dynamicRanges);

  /* ── Derived state ────────────────────────────────────── */

  const byd = isByd(bushingType);
  const isC1 = bushingType === 'C1';

  /*
   * Disable Compute only when:
   *  1. No input value has changed, and
   *  2. No configuration value has changed.
   *
   * Uploading a new SOR updates input/configuration values.
   * Therefore a successful SOR upload enables Compute.
   */
  const computeDisabled =
    !computeDirty && !configDirty;

  /* ── Handlers ─────────────────────────────────────────── */

  const handleCompute = useCallback(() => {
    const { valid, messages } = validate();

    if (!valid) {
      useUIStore
        .getState()
        .openComputeGuardDialog(messages);
      return;
    }

    useUIStore
      .getState()
      .openDialog('computeConfirmDialog');
  }, [validate]);

  const handleMenuClick = useCallback(
    (_event: React.MouseEvent<HTMLElement>) => {
      useUIStore
        .getState()
        .openDialog('configDialog');
    },
    [],
  );

  return (
    <Card
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        borderRadius: '12px',
        overflow: 'hidden',
        bgcolor: SURFACE_BG,
      }}
    >
      {/* ── Scrollable body ──────────────────────────────── */}

      <Box
        sx={{
          flex: 1,
          overflow: 'auto',
          px: 1.5,
          pt: 2.5,
          pb: 1,
          display: 'flex',
          flexDirection: 'column',
          gap: 2.5,
        }}
      >
        {/* SOR Upload */}

        <SORUpload
          onFileAccepted={handleFile}
          fileName={fileName}
          loading={sorLoading}
          disabled={byd}
          onClear={clearFile}
        />

        {/* SOR parsing error */}

        {sorError && (
          <Alert
            severity="error"
            variant="outlined"
            sx={{ py: 0 }}
          >
            {sorError}
          </Alert>
        )}

        {/* SOR Number and Bushing Type */}

        <SORComboBox
          sorValue={sorString || null}
          sorItems={sorItems}
          onSorChange={handleSorSelect}
          onSorInputChange={(v) => useInputStore.getState().setSorString(v)}
          sorDisabled={false}
          bushingType={bushingType}
          onBushingTypeChange={handleBushingTypeChange}
        />

        {/* ── Packaging Section ──────────────────────────── */}

        <SectionHeading
          icon={<InventoryIcon sx={{ fontSize: 19 }} />}
          title="Packaging"
        />

        <PackagingInputs
          values={packaging}
          onChange={setPackagingField}
          disabled={byd}
          errors={validation.packaging}
          ranges={dynamicRanges.packaging}
        />

        <CoreODGroup
          coreOdMin={packaging.coreOdMin}
          coreOdMax={packaging.coreOdMax}
          hotOd={packaging.hotOd}
          sleeveThickness={sleeveThickness}
          sleeveThicknessOptions={
            config.sleeveThickness.options
          }
          onCoreOdMinChange={(value) =>
            setPackagingField('coreOdMin', value)
          }
          onCoreOdMaxChange={(value) =>
            setPackagingField('coreOdMax', value)
          }
          onHotOdChange={(value) =>
            setPackagingField('hotOd', value)
          }
          onSleeveThicknessChange={setSleeveThickness}
          onMenuClick={handleMenuClick}
          disabled={byd}
          isByd={byd}
          errors={validation.packaging}
          coreOdRange={
            dynamicRanges.packaging.coreOdMin
          }
        />

        {/* C1-specific Collar OD */}

        {isC1 && (
          <CollarODRow
            collarOd={packaging.collarOd}
            h={packaging.h}
            onCollarOdChange={(value) =>
              setPackagingField('collarOd', value)
            }
            onHChange={(value) =>
              setPackagingField('h', value)
            }
            disabled={byd}
            collarOdError={validation.packaging.collarOd}
            hError={validation.packaging.h}
          />
        )}

        {/* ── Targets Section ────────────────────────────── */}

        <SectionHeading
          icon={<TrackChangesIcon sx={{ fontSize: 19 }} />}
          title="Targets"
        />

        <CompoundFamilyComboBox
          value={compoundFamily}
          options={config.compoundFamilies}
          onChange={setCompoundFamily}
          disabled={byd}
        />

        <TargetGrid
          config={config}
          targets={targets}
          amplitudes={amplitudes}
          linearTravel={linearTravel}
          onTargetChange={setTarget}
          onAmplitudeChange={setAmplitude}
          onLinearTravelChange={setLinearTravel}
          errors={validation.targets}
          targetRanges={dynamicRanges.targets}
        />

        {/* ── Shore A Slider ─────────────────────────────── */}

        <ShoreSlider
          value={shoreA}
          onChange={setShoreA}
        />
      </Box>

      {/* ── Configuration dialog ─────────────────────────── */}

      <ConfigDialog />

      {/* ── Missing SOR Parameters dialog ───────────────── */}

      <MissingSORDialog
        data={missingData}
      />

      {/* ── Compute confirmation dialog ─────────────────── */}

      <ComputeConfirmDialog onProceed={doCompute} />

      {/* ── Bushing type change confirmation ────────────── */}

      <BushingTypeChangeDialog
        onConfirm={confirmBushingTypeChange}
      />

      {/* ── Invalid input guard dialog ───────────────────── */}

      <ComputeGuardDialog />

      {/* ── Sticky Compute footer ────────────────────────── */}

      <Box
        sx={{
          p: 1.5,
          borderTop: '1px solid',
          borderColor: 'divider',
          flexShrink: 0,
        }}
      >
        <ComputeButton
          onClick={handleCompute}
          disabled={computeDisabled}
          loading={computing}
        />
      </Box>
    </Card>
  );
};

export default InputPanel;